/*
 * Featured Content "Trunk Show Booking"
 * (resources/views/modules/featured-content-trunk-show-booking.edge)
 *
 * - Links to "#book-your-appointment?piece=<reference>" (e.g. gallery card
 *   links) scroll to the booking form and pre-tick that piece.
 * - On load, slot statuses are read from the trunk show Google Sheet
 *   (/api/trunk-show/slots); anything not "Available" is disabled.
 * - On submit, the chosen slot / guests / pieces / notes are
 *   composed into the hidden `message` field before the shared js-form
 *   handler (forms.ts) posts to the contact pipeline.
 */

import { stardust } from '@eidellev/adonis-stardust/client';
import smoothScroll from './smooth-scrolling';

const SECTION_ID = 'book-your-appointment';

function preselectPiece(section: HTMLElement, ref: string): void {
  const wanted = ref.trim().toUpperCase();
  if (!wanted) return;
  section.querySelectorAll<HTMLInputElement>('.js-ts-piece').forEach((box) => {
    if ((box.dataset.ref || '').toUpperCase() === wanted) box.checked = true;
  });
}

function pieceFromHref(href: string): string {
  const query = href.split('?')[1] || '';
  return new URLSearchParams(query).get('piece') || '';
}

const normaliseSlot = (value: string): string =>
  String(value || '')
    .replace(/[\u2012-\u2015\u2212-]/g, '-')
    .replace(/\s+/g, '')
    .toLowerCase();

/** Disable slots the team's sheet already shows as Requested / Booked */
async function syncSlotAvailability(section: HTMLElement): Promise<void> {
  try {
    const response = await fetch(stardust.route('api.trunkShowSlots'), { credentials: 'same-origin' });
    if (!response.ok) return;
    const { slots } = (await response.json()) as { slots: { time: string; status: string }[] };
    const taken = new Set(
      (slots || []).filter((slot) => slot.status && slot.status.toLowerCase() !== 'available').map((slot) => normaliseSlot(slot.time))
    );
    const bookedLabel = section.dataset.bookedLabel || 'Booked';
    section.querySelectorAll<HTMLInputElement>('input[name="timeSlot"]').forEach((input) => {
      if (!taken.has(normaliseSlot(input.value))) return;
      input.checked = false;
      input.disabled = true;
      input.required = false;
      const label = input.closest('.ts-booking__slot');
      label?.classList.add('ts-booking__slot--booked');
      const status = label?.querySelector('.ts-booking__slot-status');
      if (status) status.textContent = bookedLabel;
    });
    // keep the radio group required while at least one slot is open
    const open = section.querySelectorAll<HTMLInputElement>('input[name="timeSlot"]:not(:disabled)');
    if (open.length) open[0].required = true;
  } catch {
    // Sheet unavailable — fall back to the statuses set in Contentful
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const section = document.querySelector<HTMLElement>('.js-ts-booking');
  if (!section) return;
  const form = section.querySelector<HTMLFormElement>('form');
  if (!form) return;

  syncSlotAvailability(section);

  // Card / CTA links that target the booking section. Capture phase + stop
  // propagation so the global jQuery hash handler (smooth-scrolling.ts) never
  // sees "#book-your-appointment?piece=…" (an invalid selector for it).
  document.addEventListener(
    'click',
    (event) => {
      const link = (event.target as HTMLElement).closest<HTMLAnchorElement>(`a[href^="#${SECTION_ID}"]`);
      if (!link) return;
      event.preventDefault();
      event.stopPropagation();
      preselectPiece(section, pieceFromHref(link.getAttribute('href') || ''));
      smoothScroll(section);
      history.replaceState(null, '', `#${SECTION_ID}`);
    },
    true
  );

  // Direct visits with ?piece=<reference> in the hash
  if (window.location.hash.startsWith(`#${SECTION_ID}`)) {
    preselectPiece(section, pieceFromHref(window.location.hash));
  }

  // Compose the hidden `message` before the shared js-form handler builds its
  // FormData. Document-level capture runs ahead of the form's own submit
  // listeners regardless of registration order.
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target !== form) return;
      const value = (name: string): string => {
        const field = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | RadioNodeList | null;
        return field ? String(field.value || '').trim() : '';
      };
      const eventName = form.querySelector<HTMLInputElement>('.js-ts-event')?.value || 'Trunk show';
      const pieceBoxes = Array.from(form.querySelectorAll<HTMLInputElement>('.js-ts-piece'));
      const pieces = pieceBoxes.filter((box) => box.checked).map((box) => `- ${box.value}`);
      const lines = [
        `${eventName} — appointment request`,
        '',
        `Requested time: ${value('timeSlot') || 'Not selected'}`,
        `Number of guests: ${value('guests') || '1'}`,
      ];
      // Pieces checklist is optional (only rendered when the module has image items)
      if (pieceBoxes.length) {
        lines.push('', 'Pieces of interest:', pieces.length ? pieces.join('\n') : '- No preference / show me the collection');
      }
      const notes = value('notes');
      if (notes) lines.push('', `Notes: ${notes}`);
      const message = form.querySelector<HTMLInputElement>('.js-ts-message');
      if (message) message.value = lines.join('\n');
    },
    true
  );
});
